/**
 * Venue onboarding draft-resume helpers.
 *
 * When a venue owner logs back in after closing the tab mid-onboarding,
 * we want to:
 *   1. Detect that they have an unsubmitted draft application
 *   2. Hydrate `sessionStorage.venueOnboarding` with every field they
 *      previously typed (across all 7 steps), so each step renders
 *      pre-filled
 *   3. Redirect them to Step 1 with a "Welcome back" toast — they can
 *      then click through each step to verify everything looks right
 *
 * Per product call: ALWAYS resume at Step 1 (not the step they reached)
 * so they can review every section. We still surface `lastStep` in the
 * toast so they know how far they got.
 */
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country dial codes match the list in VenueOnboardingStep1.js — used
// to reconstruct the `phoneCountry` object from the stored dial code.
const KNOWN_COUNTRY_CODES = [
  { code: '+30', country: 'Greece', flag: '🇬🇷' },
  { code: '+1', country: 'United States', flag: '🇺🇸' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '+32', country: 'Belgium', flag: '🇧🇪' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
  { code: '+43', country: 'Austria', flag: '🇦🇹' },
  { code: '+90', country: 'Turkey', flag: '🇹🇷' },
  { code: '+357', country: 'Cyprus', flag: '🇨🇾' },
  { code: '+353', country: 'Ireland', flag: '🇮🇪' },
];

function resolvePhoneCountry(code) {
  if (!code) return KNOWN_COUNTRY_CODES[0];
  return KNOWN_COUNTRY_CODES.find((c) => c.code === code) || { code, country: '', flag: '🌍' };
}

/**
 * Reconstruct the V2 operating-hours shape `{mo: {open, close}, ...}`
 * from the legacy `{mo: "09:00-17:00"}` / `"Closed"` stored on the doc.
 */
function reconstructOperatingHoursV2(legacy = {}) {
  const v2 = {};
  const days = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'];
  days.forEach((d) => {
    const value = legacy[d];
    if (!value || value === 'Closed') {
      v2[d] = value === 'Closed' ? { open: 'Closed', close: 'Closed' } : { open: '', close: '' };
      return;
    }
    const parts = String(value).split('-');
    if (parts.length === 2) {
      v2[d] = { open: parts[0].trim(), close: parts[1].trim() };
    } else {
      v2[d] = { open: '', close: '' };
    }
  });
  return v2;
}

/**
 * Map a server-side draft application doc into the sessionStorage shape
 * each onboarding step expects.
 */
export function mapApplicationToOnboardingSession(app) {
  if (!app || typeof app !== 'object') return {};
  return {
    firstName: app.first_name || '',
    lastName: app.last_name || '',
    phone: app.phone || '',
    phoneCountry: resolvePhoneCountry(app.phone_country_code),
    email: app.contact_email || '',
    restaurantName: app.restaurant_name || '',
    legalBusinessName: app.legal_business_name || '',
    businessVat: app.business_vat || '',
    country: app.country || '',
    region: app.region || '',
    city: app.city || '',
    zipCode: app.zip_code || '',
    street: app.street || '',
    coordinates: app.coordinates || '',
    googlePlaceId: app.google_place_id || '',
    customerPhone: app.customer_phone || '',
    website: app.website || '',
    instagram: app.instagram || '',
    facebook: app.facebook || '',
    tiktok: app.tiktok || '',
    venueType: app.venue_type || '',
    venueCategories: Array.isArray(app.venue_categories) ? app.venue_categories : [],
    venueTags: Array.isArray(app.venue_tags) ? app.venue_tags : [],
    operatingHours: app.operating_hours || {},
    operatingHoursV2: reconstructOperatingHoursV2(app.operating_hours || {}),
    introVideoUrl: app.intro_video_url || '',
    photoUrls: Array.isArray(app.photo_urls) ? app.photo_urls : [],
    rejectedPhotos: Array.isArray(app.rejected_photos) ? app.rejected_photos : [],
    rejectedVideoUrl: app.rejected_video_url || '',
    revisionFieldIssues: Array.isArray(app.revision_field_issues) ? app.revision_field_issues : [],
    revisionAdminNote: app.revision_admin_note || '',
    applicationStatus: app.status || '',
    invitedReferralId: app.invited_referral_id || '',
    invitedVenueReferralId: app.invited_referral_id || '',
  };
}

/**
 * Try to resume a venue onboarding draft for the currently logged-in
 * user. On success, hydrates sessionStorage and navigates the user to
 * `/venue/onboarding/1` with a "Welcome back" toast.
 *
 * Returns `true` if a draft was found & resume was initiated, `false`
 * otherwise. Caller should fall through to its normal routing when
 * `false` is returned.
 *
 * Safe to call for any role — if no draft exists, it's a no-op.
 */
export async function tryResumeVenueDraft({ token, navigate, showToast = true } = {}) {
  if (!token || !navigate) return false;
  try {
    const res = await axios.get(`${API}/venue/applications/my`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const app = res.data?.application;
    if (!app || !['draft', 'revisions_requested'].includes(app.status)) return false;

    const session = mapApplicationToOnboardingSession(app);
    sessionStorage.setItem('venueOnboarding', JSON.stringify(session));

    const lastStep = Math.max(1, Math.min(2, Number(app.current_step) || 1));
    if (showToast) {
      const stepNote = lastStep > 1 ? ` (you'd reached Step ${lastStep})` : '';
      toast.success(`Welcome back — your draft is ready${stepNote}.`, { duration: 4500 });
    }
    navigate('/venue/onboarding/1', { replace: true });
    return true;
  } catch (err) {
    // Not authed, no application, or transient error — silently fall through.
    return false;
  }
}


/**
 * Map the `sessionStorage.venueOnboarding` object into the snake-cased
 * payload shape `POST /api/venue/applications` expects. Used by every
 * step's "auto-save on Continue" path AND by the "Save & exit" button.
 */
export function mapOnboardingSessionToPayload(data = {}, step) {
  return {
    first_name: data.firstName || '',
    last_name: data.lastName || '',
    phone_country_code: data.phoneCountry?.code || '+30',
    phone: data.phone || '',
    contact_email: data.email || '',
    restaurant_name: data.restaurantName || '',
    legal_business_name: data.legalBusinessName || '',
    business_vat: data.businessVat || '',
    country: data.country || '',
    region: data.region || '',
    city: data.city || '',
    zip_code: data.zipCode || '',
    street: data.street || '',
    coordinates: data.coordinates || '',
    google_place_id: data.googlePlaceId || '',
    customer_phone: data.customerPhone || '',
    website: data.website || '',
    instagram: data.instagram || '',
    facebook: data.facebook || '',
    tiktok: data.tiktok || '',
    venue_type: data.venueType || '',
    venue_categories: Array.isArray(data.venueCategories) ? data.venueCategories : [],
    venue_tags: Array.isArray(data.venueTags) ? data.venueTags : [],
    operating_hours: data.operatingHours || {},
    intro_video_url: data.introVideoUrl || '',
    invited_referral_id: data.invitedVenueReferralId || '',
    step: typeof step === 'number' ? step : 1,
    submitted: false,
  };
}

/**
 * Persist the current sessionStorage onboarding draft to the backend
 * without submitting. Used by both auto-save (on Continue) and the
 * explicit "Save & exit" button.
 *
 * @param {number} step - current step number (1-7)
 * @param {{ silent?: boolean }} options - silent disables error toasts
 * @returns {Promise<{ok: boolean, error?: any}>}
 */
export async function saveVenueDraftToBackend(step, { silent = false } = {}) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return { ok: false };
    const allData = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');
    await axios.post(
      `${API}/venue/applications`,
      mapOnboardingSessionToPayload(allData, step),
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return { ok: true };
  } catch (err) {
    if (!silent) {
      toast.error("Couldn't save your progress. Please check your connection.");
    }
    return { ok: false, error: err };
  }
}
